import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

/**
 * Arbiter: link a tournament from the FMJD Tournament Manager (tm.fmjd.org).
 *
 * TM keeps the entries, pairs and ranks; FMJD Play plays the games. Linking
 * matches TM's players to FMJD Play accounts by FMJD ID and lists anyone who
 * cannot play here yet, so the arbiter can act before round one.
 */

interface TmEvent {
  uid: string;
  name: string;
  state: string;
  total_rounds: number;
  starts_at: string | null;
  linkedSlug: string | null;
}

interface Report {
  matched: number;
  noAccount: { tmPlayerId: number; name: string; fmjdId: string | null }[];
  noFmjdId: { tmPlayerId: number; name: string }[];
}

export function TmLink() {
  const [events, setEvents] = useState<TmEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<{ uid: string; slug: string; report: Report } | null>(null);

  async function load() {
    const r = await fetch("/api/admin/tm/tournaments");
    const d = await r.json().catch(() => null);
    if (!r.ok) {
      setError(
        d?.error === "tm_key_missing"
          ? "FMJD Play has no key for the Tournament Manager yet. The owner makes it in TM (Admin, Users, Services) and adds it to FMJD Play's settings as TM_API_KEY."
          : d?.error === "forbidden"
            ? "Only arbiters can link tournaments."
            : "The Tournament Manager cannot be reached right now."
      );
      return;
    }
    setEvents(d.tournaments);
  }

  useEffect(() => {
    void load();
  }, []);

  async function link(uid: string) {
    setBusy(uid);
    const r = await fetch("/api/admin/tm/link", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ uid }),
    });
    const d = await r.json().catch(() => null);
    setBusy(null);
    if (!r.ok) {
      setError("Linking failed: the Tournament Manager did not answer.");
      return;
    }
    setResult({ uid, slug: d.slug, report: d.report });
    await load();
  }

  return (
    <div style={{ maxWidth: 820 }}>
      <h1 style={{ marginTop: 0, fontSize: 24 }}>Tournaments from the Tournament Manager</h1>
      <p style={{ color: "#555", lineHeight: 1.6 }}>
        Online tournaments set up in the FMJD Tournament Manager (tm.fmjd.org). Linking one lets play.fmjd.org play its
        games: TM keeps the entries, pairs each round and keeps the official results and standings.
      </p>

      {error && <p style={{ color: "#c62828" }}>{error}</p>}
      {!events && !error && <p style={{ color: "#666" }}>Loading…</p>}
      {events && events.length === 0 && <p style={{ color: "#666" }}>No online tournaments in TM yet.</p>}

      {events && events.length > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ textAlign: "left", borderBottom: "2px solid #ddd" }}>
              <th style={th}>Tournament</th>
              <th style={th}>Rounds</th>
              <th style={th}>Starts</th>
              <th style={th}>State in TM</th>
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {events.map((e) => (
              <tr key={e.uid} style={{ borderBottom: "1px solid #eee" }}>
                <td style={td}>{e.name}</td>
                <td style={td}>{e.total_rounds}</td>
                <td style={td}>{e.starts_at ? new Date(e.starts_at).toLocaleString() : "not set"}</td>
                <td style={td}>{e.state}</td>
                <td style={td}>
                  {e.linkedSlug && <Link to={`/t/${e.linkedSlug}/live`}>Open</Link>}{" "}
                  <button disabled={busy === e.uid} onClick={() => void link(e.uid)} style={btn}>
                    {e.linkedSlug ? "Update players" : "Link"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {result && (
        <div style={{ marginTop: 20, padding: "12px 16px", border: "1px solid #e0e0e0", borderRadius: 8 }}>
          <strong>{result.report.matched} players can play.</strong>
          {result.report.noAccount.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ color: "#c62828" }}>
                No FMJD Play account with their FMJD ID ({result.report.noAccount.length}). They cannot be paired here
                until they have one:
              </div>
              <ul>
                {result.report.noAccount.map((p) => (
                  <li key={p.tmPlayerId}>
                    {p.name} (FMJD ID {p.fmjdId})
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.report.noFmjdId.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={{ color: "#c62828" }}>Entered in TM without an FMJD ID ({result.report.noFmjdId.length}):</div>
              <ul>
                {result.report.noFmjdId.map((p) => (
                  <li key={p.tmPlayerId}>{p.name}</li>
                ))}
              </ul>
            </div>
          )}
          <div style={{ marginTop: 8 }}>
            <Link to={`/t/${result.slug}/live`}>Open the tournament</Link>
          </div>
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = { padding: "6px 8px", fontWeight: 600 };
const td: React.CSSProperties = { padding: "6px 8px" };
const btn: React.CSSProperties = {
  padding: "4px 12px",
  fontSize: 13,
  border: "1px solid #ccc",
  borderRadius: 6,
  background: "#fff",
  cursor: "pointer",
};
